import { useMemo } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useForecastMeeting, type AeForecastRow, type ItemOfBusiness } from "../api";
import { AeForecastDrawer } from "../components/AeForecastDrawer";
import { CroForecastFilterBar } from "../components/FilterBar";
import { LockedBanner } from "../components/LockedBanner";
import { NotesArea } from "../components/NotesArea";
import { CroForecastSubNav } from "../components/SubNav";
import { fmtCents, fmtCentsWhole, fmtMonthName } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

/**
 * Forecast Meeting tab — modeled on HubSpot's /forecasting UI.
 *
 * Each rep row shows the headline columns Stan reviews in the meeting:
 *   Name · Total Pipeline · Coverage · per-stage closed · Forecast Submission · Update
 *
 * The per-AE table uses the shared cinch DataTable (clickable rows, zebra
 * striping, sticky header) and renders a totals row in <TableFooter> via
 * DataTable's ``footer`` slot — sums Total Pipeline / per-month closed /
 * Forecast Submission so Stan can see the team roll-up at a glance.
 *
 * Clicking a row (or its Update button) opens the EntityDrawer with the
 * per-AE editor — the cinch_ops standard "quick → expanded" drawer UX
 * used by renewals and Open Deals.
 */
export function CroForecastForecastMeetingPage() {
  const search = useSearch({ from: "/cro-forecast/forecast-meeting" }) as CroForecastSearch;
  const navigate = useNavigate();
  const { year, week } = resolveSearch(search);
  const { data, isLoading, error } = useForecastMeeting(year, week);
  const selectedRepId = search.rep ?? null;
  const selectedRep = data?.reps.find((r) => r.rep_id === selectedRepId) ?? null;

  function openRep(rep_id: string) {
    void navigate({
      to: "/cro-forecast/forecast-meeting",
      search: (prev: Record<string, unknown>) => ({ ...(prev ?? {}), rep: rep_id }),
      replace: true,
    });
  }

  // Build column list — months are dynamic per quarter, so we derive columns
  // from data.months. Memoised against data so the table doesn't reshape on
  // every re-render.
  const currentFiscalMonth = data?.fiscal_month ?? 0;
  const columns = useMemo<DataTableColumn<AeForecastRow>[]>(() => {
    if (!data) return [];
    const monthCols: DataTableColumn<AeForecastRow>[] = data.months.map((m) => ({
      key: `closed_${m}`,
      header: `${fmtMonthName(m)} closed`,
      numeric: true,
      align: "right",
      render: (rep) => {
        const md = rep.months.find((x) => x.month === m);
        return (
          <span className="text-xs text-muted-foreground">
            {fmtCents(md?.closed_cents ?? 0)}
          </span>
        );
      },
    }));
    return [
      {
        key: "name",
        header: "Name",
        render: (rep) => (
          <span className="flex flex-col">
            <span className="font-medium">{rep.display_name}</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {rep.tier.replace(/_/g, " ")}
            </span>
          </span>
        ),
      },
      {
        key: "quarter_pipeline_cents",
        header: "Total Pipeline",
        numeric: true,
        align: "right",
        render: (rep) => fmtCents(rep.quarter_pipeline_cents),
      },
      {
        key: "quarter_close_pipeline_cents",
        header: "Quarter Pipeline",
        numeric: true,
        align: "right",
        // Only deals forecast to CLOSE in this quarter — the coverage
        // denominator's counterpart. Long-cycle reps carry a big total
        // pipeline whose close dates land in later quarters.
        render: (rep) => fmtCents(rep.quarter_close_pipeline_cents),
      },
      {
        key: "quarter_coverage",
        header: "Coverage",
        numeric: true,
        align: "right",
        // Quarter pipeline ÷ quarter call, NOT total pipeline ÷ call —
        // measuring a quarterly call against an all-time book flatters
        // coverage for reps with deals stacked in future quarters.
        render: (rep) => (
          <span className="text-xs">
            {rep.quarter_coverage != null
              ? `${rep.quarter_coverage.toFixed(2)}×`
              : "—"}
          </span>
        ),
      },
      ...monthCols,
      {
        key: "month_submission_cents",
        header: `${fmtMonthName(currentFiscalMonth)} Forecast Submission`,
        numeric: true,
        align: "right",
        // Whole-number, comma-separated — has to be comparable to
        // HubSpot's Forecast Submission UI to the dollar, so no K/M
        // abbreviation here.
        render: (rep) => fmtCentsWhole(rep.month_submission_cents),
      },
      {
        key: "submission_wow_delta_cents",
        header: "WoW",
        align: "right",
        // How the AE moved their own call since last week, measured against last
        // week's snapshot of this table. No chip when there's nothing
        // comparable — a first-ever call isn't an increase.
        render: (rep) => (
          <DeltaCell value={rep.submission_wow_delta_cents} chip />
        ),
      },
      {
        key: "quarter_submission_cents",
        header: "Quarter Forecast Submission",
        numeric: true,
        align: "right",
        // Every month of the quarter the AE has called, summed. A partner rep
        // whose deals all close later in the quarter reads $0 on the month
        // column but carries a real number here.
        render: (rep) => fmtCentsWhole(rep.quarter_submission_cents),
      },
      {
        key: "quarter_submission_wow_delta_cents",
        header: "Quarter WoW",
        align: "right",
        // The quarter call's movement, same snapshot baseline as the month WoW.
        render: (rep) => (
          <DeltaCell value={rep.quarter_submission_wow_delta_cents} chip />
        ),
      },
      {
        key: "_update",
        header: "Update",
        align: "right",
        render: (rep) => (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              openRep(rep.rep_id);
            }}
          >
            Update
          </Button>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Team-roll-up totals — rendered in <TableFooter> beneath the body so
  // Stan sees the bottom-line numbers next to the per-AE rows.
  const totals = useMemo(() => {
    if (!data) return null;
    const pipeline = data.reps.reduce(
      (acc, r) => acc + (r.quarter_pipeline_cents ?? 0),
      0,
    );
    const quarterPipeline = data.reps.reduce(
      (acc, r) => acc + (r.quarter_close_pipeline_cents ?? 0),
      0,
    );
    const submission = data.reps.reduce(
      (acc, r) => acc + (r.month_submission_cents ?? 0),
      0,
    );
    const quarterSubmission = data.reps.reduce(
      (acc, r) => acc + (r.quarter_submission_cents ?? 0),
      0,
    );
    const callTotal = data.reps.reduce(
      (acc, r) => acc + (r.quarter_call_cents ?? 0),
      0,
    );
    const closedByMonth = new Map<number, number>();
    for (const m of data.months) closedByMonth.set(m, 0);
    for (const rep of data.reps) {
      for (const md of rep.months) {
        closedByMonth.set(
          md.month,
          (closedByMonth.get(md.month) ?? 0) + (md.closed_cents ?? 0),
        );
      }
    }
    // Coverage at the team level = QUARTER pipeline / total call, matching the
    // per-rep column. Falls back to null when no calls exist so we render an
    // em-dash instead of "Infinity×".
    const coverage = callTotal > 0 ? quarterPipeline / callTotal : null;
    return {
      pipeline,
      quarterPipeline,
      submission,
      quarterSubmission,
      coverage,
      closedByMonth,
    };
  }, [data]);

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex flex-col gap-6 p-4 md:p-6">
        <LockedBanner
          status={data?.lock_status}
          isoYear={year}
          isoWeek={week}
        />
        {error && (
          <p className="text-sm text-destructive">
            Failed to load: {(error as Error).message}
          </p>
        )}
        {(isLoading || data) && (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Per-AE Forecast — Q{data?.quarter} {data?.quarter_year}
                </CardTitle>
                {/* The pipeline columns are SQO-on, matching the Summary banner.
                    Without saying so, a rep's Total Pipeline here reads lower
                    than their HubSpot deal list and looks like a bug. */}
                <p className="text-[11px] text-muted-foreground">
                  Pipeline columns count Sales-Qualified-onward deals. Pre-qualified
                  Sales Pipeline deals are excluded; partner and CS pipelines count
                  every open stage.
                </p>
              </CardHeader>
              <CardContent>
                <DataTable<AeForecastRow>
                  columns={columns}
                  rows={data?.reps ?? []}
                  rowKey={(r) => r.rep_id}
                  loading={isLoading}
                  onRowClick={(r) => openRep(r.rep_id)}
                  selectedKey={selectedRepId}
                  emptyMessage="No active reps for this quarter yet."
                  containerClassName="max-h-[calc(100vh-22rem)]"
                  footer={(cols) =>
                    totals && data ? (
                      <TableRow>
                        {cols.map((c) => (
                          <TableCell
                            key={c.key}
                            numeric={c.numeric}
                            align={c.align}
                            className="font-semibold"
                          >
                            {renderTotalCell(c.key, totals, data.months)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ) : null
                  }
                />
              </CardContent>
            </Card>

            {data && <ItemsOfBusinessCard items={data.items_of_business} />}

            {data && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <NotesArea
                  isoYear={year}
                  isoWeek={week}
                  scope="meeting_agenda"
                  initialBody={data.agenda_markdown ?? ""}
                  label="Meeting agenda (markdown)"
                  placeholder={"## Agenda\n- Open with team pipeline pacing\n- ..."}
                  minHeightPx={220}
                />
                <NotesArea
                  isoYear={year}
                  isoWeek={week}
                  scope="meeting_summary"
                  initialBody={data.meeting_summary_markdown ?? ""}
                  label="Meeting summary / takeaways"
                  placeholder="Recap after the meeting — decisions, action items, follow-ups."
                  minHeightPx={220}
                />
              </div>
            )}
          </>
        )}
      </div>

      <AeForecastDrawer
        rep={selectedRep}
        perAeNotesBlob={data ? parsePerAeNotes(data.reps) : {}}
        isoYear={year}
        isoWeek={week}
        targetYear={data?.quarter_year ?? year}
        currentFiscalMonth={currentFiscalMonth}
      />
    </div>
  );
}

/** Map a column key to its corresponding totals-row cell content. */
function renderTotalCell(
  key: string,
  totals: {
    pipeline: number;
    quarterPipeline: number;
    submission: number;
    quarterSubmission: number;
    coverage: number | null;
    closedByMonth: Map<number, number>;
  },
  _months: number[],
): React.ReactNode {
  if (key === "name") {
    return <span className="uppercase tracking-wider text-xs">Team total</span>;
  }
  if (key === "quarter_pipeline_cents") return fmtCents(totals.pipeline);
  if (key === "quarter_close_pipeline_cents") {
    return fmtCents(totals.quarterPipeline);
  }
  if (key === "quarter_coverage") {
    return (
      <span className="text-xs">
        {totals.coverage != null ? `${totals.coverage.toFixed(2)}×` : "—"}
      </span>
    );
  }
  if (key === "month_submission_cents") return fmtCentsWhole(totals.submission);
  if (key === "quarter_submission_cents") {
    return fmtCentsWhole(totals.quarterSubmission);
  }
  // WoW is a per-rep movement; a team-level sum of deltas isn't meaningful —
  // reps with no comparable prior week would silently drop out of the sum.
  if (
    key === "submission_wow_delta_cents" ||
    key === "quarter_submission_wow_delta_cents"
  ) {
    return null;
  }
  if (key.startsWith("closed_")) {
    const m = Number(key.slice("closed_".length));
    return (
      <span className="text-xs text-muted-foreground">
        {fmtCents(totals.closedByMonth.get(m) ?? 0)}
      </span>
    );
  }
  return "";
}

function parsePerAeNotes(reps: { rep_id: string; notes: string | null }[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of reps) {
    if (r.notes) out[r.rep_id] = r.notes;
  }
  return out;
}

function ItemsOfBusinessCard({ items }: { items: ItemOfBusiness[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          Items of business
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No items yet — they'll appear here as the meeting progresses or as
            Phase-2 rules flag deals that need discussion (stage misalignment,
            stuck deals, big swings).
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="text-xs">
                    <StatusBadge value={it.item_type} label={it.item_type} />
                  </TableCell>
                  <TableCell className="font-medium">{it.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {it.detail ?? "—"}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    {it.resolved ? (
                      <StatusBadge value="resolved" label="Resolved" />
                    ) : (
                      <StatusBadge value="pending" label="Open" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
